import React, { useState, useEffect } from 'react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * MIQ Trend Chart — Mini line chart showing MIQ score over past weeks.
 * Pure SVG, no external dependencies.
 */
const MIQTrendChart = ({ userId, currentScore, isCalibrating, calibrationDay }) => {
  const [scores, setScores] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!userId) return;
    const fetchHistory = async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/miq-score/history/${userId}?limit=12`);
        const data = await res.json();
        if (data.success && data.scores?.length > 0) {
          setScores(data.scores);
        }
      } catch (e) {
        console.error('Failed to load MIQ score history:', e);
      } finally {
        setLoading(false);
      }
    };
    fetchHistory();
  }, [userId]);

  // Build data points: score history + current week
  const dataPoints = scores.map(s => ({
    week: s.week_number,
    score: s.score,
    delta: s.delta
  }));

  // Always ensure the chart ends at the current real-time score
  // This prevents stale historical data from misrepresenting the user's actual score
  if (currentScore && dataPoints.length > 0) {
    const lastPoint = dataPoints[dataPoints.length - 1];
    if (Math.abs(lastPoint.score - currentScore) > 1) {
      // The stored score diverges from real-time score — update the last point
      lastPoint.score = currentScore;
      lastPoint.delta = dataPoints.length >= 2 
        ? Math.round((currentScore - dataPoints[dataPoints.length - 2].score) * 10) / 10 
        : 0;
    }
  }

  // If we're still calibrating with no history, show calibration progress
  if (isCalibrating && dataPoints.length === 0) {
    return (
      <div data-testid="miq-trend-chart-calibrating" style={{
        background: 'rgba(255,255,255,0.04)',
        borderRadius: '16px',
        padding: '20px',
        marginBottom: '16px'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
          <span style={{ fontSize: '14px', fontWeight: '600', color: '#e0e0e0', letterSpacing: '0.5px' }}>MIQ TREND</span>
          <span style={{ fontSize: '12px', color: '#999' }}>Calibrating... Day {calibrationDay}/30</span>
        </div>
        <div style={{ height: '4px', background: 'rgba(255,255,255,0.1)', borderRadius: '2px', overflow: 'hidden' }}>
          <div style={{
            width: `${Math.min(100, ((calibrationDay || 1) / 30) * 100)}%`,
            height: '100%',
            background: 'linear-gradient(90deg, #ff9800, #ffb74d)',
            borderRadius: '2px',
            transition: 'width 0.5s ease'
          }} />
        </div>
        <p style={{ fontSize: '12px', color: '#777', marginTop: '10px', textAlign: 'center' }}>
          Weekly score trends appear after calibration
        </p>
      </div>
    );
  }

  // No history and not calibrating — show current score with updated message
  if (dataPoints.length === 0 && !isCalibrating) {
    const projectedPoints = [
      { week: 0, score: currentScore || 50, delta: 0 },
      { week: 1, score: currentScore || 50, delta: 0 }
    ];
    return (
      <div data-testid="miq-trend-chart" style={{
        background: 'rgba(255,255,255,0.04)',
        borderRadius: '16px',
        padding: '20px',
        marginBottom: '16px'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <span style={{ fontSize: '14px', fontWeight: '600', color: '#e0e0e0', letterSpacing: '0.5px' }}>MIQ TREND</span>
          <span style={{ fontSize: '12px', color: '#999' }}>Building history...</span>
        </div>
        <TrendSVG points={projectedPoints} currentScore={currentScore || 50} />
        <p style={{ fontSize: '12px', color: '#777', marginTop: '10px', textAlign: 'center' }}>
          Your score updates daily as you log meals and check in
        </p>
      </div>
    );
  }

  // We have history — show the chart
  const latestDelta = dataPoints.length > 0 ? dataPoints[dataPoints.length - 1].delta : 0;

  return (
    <div data-testid="miq-trend-chart" style={{
      background: 'rgba(255,255,255,0.04)',
      borderRadius: '16px',
      padding: '20px',
      marginBottom: '16px'
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <span style={{ fontSize: '14px', fontWeight: '600', color: '#e0e0e0', letterSpacing: '0.5px' }}>MIQ TREND</span>
        {latestDelta !== 0 && (
          <span data-testid="miq-trend-delta" style={{
            fontSize: '13px',
            fontWeight: '600',
            color: latestDelta > 0 ? '#38ef7d' : '#f44336',
            background: latestDelta > 0 ? 'rgba(56,239,125,0.12)' : 'rgba(244,67,54,0.12)',
            padding: '3px 10px',
            borderRadius: '12px'
          }}>
            {latestDelta > 0 ? '+' : ''}{latestDelta} this week
          </span>
        )}
      </div>
      <TrendSVG points={dataPoints} currentScore={currentScore} />
    </div>
  );
};

/**
 * Pure SVG line chart with gradient fill
 */
const TrendSVG = ({ points, currentScore }) => {
  if (!points || points.length === 0) return null;

  const width = 320;
  const height = 120;
  const padding = { top: 20, right: 30, bottom: 25, left: 35 };
  const chartW = width - padding.left - padding.right;
  const chartH = height - padding.top - padding.bottom;

  // Calculate bounds
  const allScores = points.map(p => p.score);
  const minScore = Math.max(0, Math.min(...allScores) - 5);
  const maxScore = Math.min(100, Math.max(...allScores) + 5);
  const scoreRange = maxScore - minScore || 10;

  // Map points to SVG coordinates
  const svgPoints = points.map((p, i) => ({
    x: padding.left + (i / Math.max(1, points.length - 1)) * chartW,
    y: padding.top + chartH - ((p.score - minScore) / scoreRange) * chartH,
    score: p.score,
    week: p.week,
    delta: p.delta
  }));

  // Build path
  const linePath = svgPoints.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ');
  
  // Fill area path
  const areaPath = linePath + 
    ` L ${svgPoints[svgPoints.length - 1].x} ${padding.top + chartH}` +
    ` L ${svgPoints[0].x} ${padding.top + chartH} Z`;

  // Determine overall trend color
  const trendUp = points.length >= 2 && points[points.length - 1].score >= points[0].score;
  const lineColor = trendUp ? '#38ef7d' : '#f44336';
  const fillId = `trend-fill-${trendUp ? 'up' : 'down'}`;

  // Y-axis labels (3 ticks)
  const yTicks = [minScore, Math.round((minScore + maxScore) / 2), maxScore];

  return (
    <svg 
      viewBox={`0 0 ${width} ${height}`} 
      style={{ width: '100%', height: 'auto' }}
      data-testid="miq-trend-svg"
    >
      <defs>
        <linearGradient id={fillId} x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor={lineColor} stopOpacity="0.25" />
          <stop offset="100%" stopColor={lineColor} stopOpacity="0.02" />
        </linearGradient>
      </defs>

      {/* Grid lines */}
      {yTicks.map((tick, i) => {
        const y = padding.top + chartH - ((tick - minScore) / scoreRange) * chartH;
        return (
          <g key={i}>
            <line x1={padding.left} y1={y} x2={padding.left + chartW} y2={y} 
                  stroke="rgba(255,255,255,0.06)" strokeWidth="1" />
            <text x={padding.left - 8} y={y + 4} 
                  fill="#666" fontSize="9" textAnchor="end">{tick}</text>
          </g>
        );
      })}

      {/* Area fill */}
      <path d={areaPath} fill={`url(#${fillId})`} />

      {/* Line */}
      <path d={linePath} fill="none" stroke={lineColor} strokeWidth="2.5" 
            strokeLinecap="round" strokeLinejoin="round" />

      {/* Data points */}
      {svgPoints.map((p, i) => (
        <g key={i}>
          <circle cx={p.x} cy={p.y} r="4" fill="#1a1a2e" stroke={lineColor} strokeWidth="2" />
          {/* Score label on last point */}
          {i === svgPoints.length - 1 && (
            <text x={p.x + 8} y={p.y + 4} fill={lineColor} fontSize="11" fontWeight="700">
              {p.score}
            </text>
          )}
        </g>
      ))}

      {/* Week labels */}
      {svgPoints.length <= 8 && svgPoints.map((p, i) => (
        <text key={`wk-${i}`} x={p.x} y={height - 4} 
              fill="#555" fontSize="8" textAnchor="middle">
          W{p.week}
        </text>
      ))}
      {svgPoints.length > 8 && (
        <>
          <text x={svgPoints[0].x} y={height - 4} fill="#555" fontSize="8" textAnchor="middle">
            W{svgPoints[0].week}
          </text>
          <text x={svgPoints[svgPoints.length - 1].x} y={height - 4} fill="#555" fontSize="8" textAnchor="middle">
            W{svgPoints[svgPoints.length - 1].week}
          </text>
        </>
      )}
    </svg>
  );
};

export default MIQTrendChart;
